// Zentrale UI-Konstanten fuer Frontend
// Vermeidet Abweichungen zwischen Komponenten (z. B. Taskleiste/Fenster)

export const TASKBAR_HEIGHT = 50
export const TASKBAR_WIDTH = 90
export const DEFAULT_WINDOW_WIDTH = 900
export const DEFAULT_WINDOW_HEIGHT = 550
export const WINDOW_TITLEBAR_HEIGHT = 36
export const WINDOW_CASCADE_OFFSET_X = 36
export const WINDOW_CASCADE_OFFSET_Y = WINDOW_TITLEBAR_HEIGHT
export const MIN_WINDOW_WIDTH = 640  // Angepasst für Sidebar + Content Card Layout
export const MIN_WINDOW_HEIGHT = 360 // Erweitert fuer bessere Darstellung

// Goldener Schnitt — Fenster öffnen als goldenes Rechteck (Breite : Höhe = φ : 1)
export const GOLDEN_RATIO = 1.618

// App-spezifische Größenkonfiguration.
// `widthFraction` = Anteil der Viewport-Breite; die Höhe wird aus dem goldenen
// Schnitt abgeleitet (height = width / φ) und auf den verfügbaren Platz sowie
// die Mindesthöhe begrenzt. So bleiben Fenster ausgewogen und nicht zu groß.
type WindowSizeConfig = {
  widthFraction: number
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

const APP_SIZE_CONFIGS: Record<string, WindowSizeConfig> = {
  'communicator2': { widthFraction: 0.44, minWidth: 480, minHeight: 500 },
  'app-store': { widthFraction: 0.5, minWidth: 640, minHeight: 440 },
  'cortex': { widthFraction: 0.54, minWidth: 650, minHeight: 540 },
  'settings': { widthFraction: 0.44, minWidth: 640, minHeight: 440 },
  'file-preview': { widthFraction: 0.52, minWidth: 640, minHeight: 540 },
  // Sidebar plus a two-column screen (#1602): the screens switch to two
  // columns at a content width of 48rem (@3xl); sidebar, gaps and frame take
  // another ~330 px, so the window needs about 1120 px.
  'ai-knowledge-extractor': { widthFraction: 0.72, minWidth: 1120, minHeight: 620 },
  'knowledge-verifier': { widthFraction: 0.57, minWidth: 700, minHeight: 540 },
  'wissenshaus': { widthFraction: 0.62, minWidth: 720, minHeight: 520 },
  'harness': { widthFraction: 0.55, minWidth: 680, minHeight: 520 },
  // Sidebar plus list or three kanban columns (#1689).
  'cdb-control': { widthFraction: 0.62, minWidth: 960, minHeight: 560 },
  // Default für unbekannte Apps
  default: { widthFraction: 0.44, minWidth: MIN_WINDOW_WIDTH, minHeight: MIN_WINDOW_HEIGHT },
}

/**
 * Berechnet die responsive Öffnungsgröße eines Fensters als goldenes Rechteck.
 * Breite = Anteil der Viewport-Breite, Höhe = Breite / φ, beides auf den
 * verfügbaren Platz und Mindest-/Maximalgrößen begrenzt.
 * @param appId - ID der Anwendung
 * @param viewportWidth - Breite des Viewports (default: window.innerWidth)
 * @param viewportHeight - Höhe des Viewports (default: window.innerHeight)
 * @returns { w: number, h: number } - Berechnete Fensterbreite und -höhe
 */
export function calculateResponsiveWindowSize(
  appId?: string,
  viewportWidth?: number,
  viewportHeight?: number
): { w: number; h: number } {
  const vw = viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1920)
  const vh = viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 1080)

  // Verfügbare Höhe ohne Taskbar, mit etwas Rand
  const availableWidth = vw - 40 // 20px Rand links/rechts
  const availableHeight = vh - TASKBAR_HEIGHT - 40 // 20px Rand oben/unten

  const config = appId ? (APP_SIZE_CONFIGS[appId] || APP_SIZE_CONFIGS.default) : APP_SIZE_CONFIGS.default

  // Breite aus Viewport-Anteil, Höhe aus dem goldenen Schnitt ableiten
  let width = Math.round(vw * config.widthFraction)
  let height = Math.round(width / GOLDEN_RATIO)

  // Wenn die goldene Höhe nicht in den Viewport passt, von der Höhe her rechnen
  if (height > availableHeight) {
    height = availableHeight
    width = Math.round(height * GOLDEN_RATIO)
  }

  // Min/Max Constraints anwenden
  if (config.minWidth) width = Math.max(width, config.minWidth)
  if (config.minHeight) height = Math.max(height, config.minHeight)
  if (config.maxWidth) width = Math.min(width, config.maxWidth)
  if (config.maxHeight) height = Math.min(height, config.maxHeight)

  // Nie größer als der verfügbare Platz
  width = Math.min(width, availableWidth)
  height = Math.min(height, availableHeight)

  // Absolute Mindestgrößen
  width = Math.max(width, MIN_WINDOW_WIDTH)
  height = Math.max(height, MIN_WINDOW_HEIGHT)

  return { w: width, h: height }
}

/**
 * Berechnet die initiale Position fÃ¼r ein neues Fenster
 * @param windowWidth - Breite des Fensters
 * @param windowHeight - HÃ¶he des Fensters
 * @param viewportWidth - Breite des Viewports
 * @param viewportHeight - HÃ¶he des Viewports
 * @returns { x: number, y: number } - Berechnete Position
 */
export function calculateCenteredWindowPosition(
  windowWidth: number,
  windowHeight: number,
  viewportWidth?: number,
  viewportHeight?: number
): { x: number; y: number } {
  const vw = viewportWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1920)
  const vh = viewportHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 1080)
  const availableHeight = vh - TASKBAR_HEIGHT

  const x = Math.max(0, Math.floor((vw - windowWidth) / 2))
  const y = Math.max(0, Math.floor((availableHeight - windowHeight) / 2))

  return { x, y }
}
